import React, { useRef, useLayoutEffect, useState } from "react";
import * as styles from "./switch.module.scss";
import classnames from "gpages/utils/classnames";
import Clickable from "gpages/components/clickable";

function updateLangInPathname(pathname, appName, lang, langOptions) {
  const hasTrailingSlash = pathname.endsWith("/");

  // 拆成路径段，过滤空段，避免多余斜杠
  const segments = pathname.split("/").filter(Boolean);

  let i = 0;
  const resultSegments = [];

  // 判断 appName
  if (segments[0] === appName) {
    resultSegments.push(segments[0]);
    i = 1;
  }

  // 判断语言码
  if (segments[i] && langOptions.includes(segments[i])) {
    if (segments[i] !== lang) {
      resultSegments.push(lang); // 替换语言码
    } else {
      resultSegments.push(segments[i]); // 保留原语言码
    }
    i += 1;
  } else {
    // 没有语言码，插入
    resultSegments.push(lang);
  }

  // 添加剩余部分
  const rest = segments.slice(i);
  resultSegments.push(...rest);

  // 拼接结果路径
  let finalPath = "/" + resultSegments.join("/");

  // 保留结尾 /
  if (hasTrailingSlash) {
    finalPath += "/";
  }

  return finalPath;
}

function stripAppNamePrefix(pathname, appName) {
  const hasTrailingSlash = pathname.endsWith("/");

  // 拆分路径段，去掉空字符串
  const segments = pathname.split("/").filter(Boolean);

  if (segments[0] === appName) {
    segments.shift(); // 去掉 appName
  }

  let newPath = "/" + segments.join("/");

  if (hasTrailingSlash && !newPath.endsWith("/")) {
    newPath += "/";
  }

  return newPath;
}

const Switch = ({
  className,
  options = [],
  location,
  appName,
  value,
  onChange,
}) => {
  const containerRef = useRef(null);
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });
  const optionRefs = useRef([]);
  const langValues = options.map((option) => option.value);

  useLayoutEffect(() => {
    const resize = () => {
      if (!options.length) return;
      const idx = options.map((x) => x.value).indexOf(value);
      if (idx === -1) return;
      const el = optionRefs.current[idx];
      if (el && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        setSliderStyle({
          left: elRect.left - containerRect.left,
          width: elRect.width,
        });
      }
    };
    resize();

    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [value, options]);

  return (
    <div
      className={classnames(styles.switchRoot, className)}
      ref={containerRef}
    >
      <div
        className={styles.switchSlider}
        style={{ left: sliderStyle.left, width: sliderStyle.width }}
      />
      {options.map(({ label, value: v }, i) => {
        const langPathname = location
          ? updateLangInPathname(location.pathname, appName, v, langValues)
          : undefined;
        return (
          <Clickable
            key={label}
            linkRef={(el) => (optionRefs.current[i] = el)}
            className={classnames(styles.switchOption, {
              [styles.switchOptionActive]: v === value,
            })}
            onClick={(e) => {
              onChange(v, stripAppNamePrefix(langPathname, appName));
              e.preventDefault();
            }}
            href={langPathname}
          >
            {label}
          </Clickable>
        );
      })}
    </div>
  );
};

export default Switch;
